import { useState } from "react";
import { ArrowLeft, PencilLine, QuestionMark } from "@phosphor-icons/react";
import { useNavigate, useParams } from "react-router-dom";

import { ActivityLogTab, NotesTab } from "@/components";
import { useCollaborators } from "@/hooks";
import { MODAL_ROUTES } from "@/router";
import { useNavigateModal } from "@/router/useNavigateModal";
import type { Collaborator } from "@/types";
import type { Tab } from "@/ui";
import { Button, Circle, Heading, Loader, Tabs } from "@/ui";

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs font-medium uppercase tracking-wide text-neutral-400">
      {label}
    </span>
    <span className="text-sm text-neutral-900">{children}</span>
  </div>
);

const OverviewView = ({
  collaborator,
}: {
  collaborator: Collaborator | undefined;
}) => (
  <div className="grid grid-cols-1 gap-x-10 gap-y-7 pt-6 sm:grid-cols-2 lg:grid-cols-3">
    <Field label="Type">
      <span className="capitalize">{collaborator?.type}</span>
    </Field>
    <Field label="Organisation">{collaborator?.title ?? "—"}</Field>
  </div>
);

export const CollaboratorDetail = () => {
  const { collaboratorId } = useParams();
  const navigate = useNavigate();
  const navigateModal = useNavigateModal();
  const { useCollaborator } = useCollaborators();

  const { data: collaboratorResponse, isLoading } =
    useCollaborator(collaboratorId);
  const collaborator = collaboratorResponse?.data;

  const [currentTabLabel, setCurrentTabLabel] = useState<string>("Details");

  if (isLoading)
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );

  if (!collaborator)
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Heading level={2} heading="Teacher not found" />
        <Button onClick={() => navigate(-1)} className="mt-4">
          Go back
        </Button>
      </div>
    );

  const tabs: Tab[] = [
    {
      label: "Details",
      component: <OverviewView collaborator={collaborator} />,
      roles: ["admin"],
    },
    {
      label: "Activity Log",
      component: collaboratorId ? (
        <ActivityLogTab subjectType="collaborator" subjectId={collaboratorId} />
      ) : null,
      roles: ["admin"],
    },
    {
      label: "Notes",
      component: collaboratorId ? (
        <NotesTab
          endpoint={`/collaborators/${collaboratorId}/notes`}
          queryKey={["notes", "collaborator", collaboratorId]}
        />
      ) : null,
      roles: ["admin"],
    },
  ];

  return (
    <div className="flex h-full flex-col gap-6 p-4 sm:p-6 lg:p-8">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex w-fit items-center gap-2 text-sm font-medium text-neutral-400 hover:text-primary-700"
      >
        <ArrowLeft size={18} />
        Back
      </button>

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          {collaborator.avatar ? (
            <img
              src={collaborator.avatar}
              alt={collaborator.name}
              className="size-12 flex-none rounded-full object-cover"
            />
          ) : (
            <Circle className="size-12 flex-none bg-neutral-100">
              <QuestionMark size={22} className="text-neutral-400" />
            </Circle>
          )}
          <div className="min-w-0">
            <Heading level={1} heading={collaborator.name} />
            <p className="mt-1 text-sm capitalize text-neutral-500">
              {collaborator.type}
              {collaborator.title ? ` · ${collaborator.title}` : ""}
            </p>
          </div>
        </div>

        <Button
          size="sm"
          onClick={() =>
            navigateModal(MODAL_ROUTES.editCollaborator, { collaborator })
          }
        >
          <PencilLine size={16} />
          Edit
        </Button>
      </div>

      <Tabs
        tabs={tabs}
        userRole="admin"
        currentLabel={currentTabLabel}
        setCurrentLabel={setCurrentTabLabel}
      />
    </div>
  );
};
